import { useState, useEffect, useRef } from 'react';
import {
  Plus, Edit2, Trash2, Save, X, Loader2,
  ShoppingBag, Eye, EyeOff, AlertCircle, Check, Upload, ImageIcon
} from 'lucide-react';
import api from '../../services/api';

// Servizi della suite a cui un prodotto può essere associato
const AVAILABLE_SERVICES = [
  { code: 'review',   name: 'Review',        emoji: '⭐' },
  { code: 'page',     name: 'Page',          emoji: '📄' },
  { code: 'menu',     name: 'Menu Digitale', emoji: '🍽️' },
  { code: 'chat_ai',  name: 'Chat AI',       emoji: '💬' },
  { code: 'agent_ai', name: 'Agent AI',      emoji: '🤖' },
  { code: 'connect',  name: 'Connect',       emoji: '🔗' },
  { code: 'display',  name: 'Display',       emoji: '🖥️' }
];

export default function NfcProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Edit states
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const emptyProduct = {
    code: '', name: '', description: '', emoji: '', image: '',
    price_public: 0, price_partner: 0, price_range: '',
    has_variants: false, badge: '', link: '', sort_order: 0,
    item_type: 'product', color: '', services: []
  };

  const [productForm, setProductForm] = useState(emptyProduct);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const toggleService = (code) => {
    setProductForm((prev) => {
      const has = prev.services.includes(code);
      return {
        ...prev,
        services: has ? prev.services.filter((s) => s !== code) : [...prev.services, code]
      };
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Immagine troppo grande (max 5MB)');
      return;
    }
    setUploadingImage(true);
    setError(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/admin/nfc-products/upload-image`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type,
          'Authorization': `Bearer ${api.getToken()}`
        },
        body: file
      });
      const data = await res.json();
      if (data.success && data.data?.url) {
        setProductForm((prev) => ({ ...prev, image: data.data.url }));
      } else {
        setError(data.error || 'Errore upload immagine');
      }
    } catch (err) {
      setError(err.message);
    }
    setUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const prodRes = await api.request('/admin/nfc-products?includeInactive=true');
      if (prodRes.success) setProducts(prodRes.data.products || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // ==================== PRODUCTS ====================

  const saveProduct = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...productForm };
      // Pulisci campi vuoti opzionali
      if (!payload.price_range) payload.price_range = null;
      if (!payload.badge) payload.badge = null;
      if (!payload.link) payload.link = null;
      if (!payload.image) payload.image = null;
      if (!payload.color) payload.color = null;
      if (!Array.isArray(payload.services)) payload.services = [];

      let response;
      if (editingProduct) {
        response = await api.request(`/admin/nfc-products/${editingProduct}`, {
          method: 'PUT', body: JSON.stringify(payload)
        });
      } else {
        response = await api.request('/admin/nfc-products', {
          method: 'POST', body: JSON.stringify(payload)
        });
      }

      if (response.success) {
        setSuccess(editingProduct ? 'Prodotto aggiornato' : 'Prodotto creato');
        setShowProductForm(false);
        setEditingProduct(null);
        setProductForm(emptyProduct);
        fetchAll();
      } else {
        setError(response.error || 'Errore nel salvataggio');
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const deleteProduct = async (id) => {
    try {
      const response = await api.request(`/admin/nfc-products/${id}`, { method: 'DELETE' });
      if (response.success) {
        setSuccess('Prodotto disattivato');
        setDeleteConfirm(null);
        fetchAll();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleProduct = async (product) => {
    try {
      await api.request(`/admin/nfc-products/${product.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !product.is_active })
      });
      fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 flex items-center justify-between">
          <div className="flex items-center gap-2"><AlertCircle className="w-5 h-5" /><span>{error}</span></div>
          <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 flex items-center gap-2">
          <Check className="w-5 h-5" /><span>{success}</span>
        </div>
      )}

      {/* ==================== PRODOTTI NFC ==================== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Prodotti & Servizi</h3>
            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs">{products.length}</span>
          </div>
          {!showProductForm && (
            <button
              onClick={() => { setShowProductForm(true); setEditingProduct(null); setProductForm(emptyProduct); }}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" /> Nuovo Prodotto
            </button>
          )}
        </div>

        {/* Product Form */}
        {showProductForm && (
          <div className="p-6 bg-indigo-50 border-b-2 border-indigo-200">
            <h4 className="font-semibold text-indigo-900 mb-4">
              {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
            </h4>
            {/* Tipo: Prodotto / Servizio */}
            <div className="flex items-center gap-4 mb-4">
              <label className="block text-xs font-medium text-gray-600">Tipo</label>
              <div className="flex bg-white rounded-lg border overflow-hidden">
                {[
                  { value: 'product', label: 'Prodotto', icon: '📦' },
                  { value: 'service', label: 'Servizio', icon: '⚙️' }
                ].map((t) => (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setProductForm({ ...productForm, item_type: t.value })}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${
                      productForm.item_type === t.value
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <span>{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Codice *</label>
                <input type="text" value={productForm.code} onChange={(e) => setProductForm({ ...productForm, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="stand-legno" disabled={!!editingProduct} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
                <input type="text" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Stand Legno" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Emoji</label>
                <input type="text" value={productForm.emoji} onChange={(e) => setProductForm({ ...productForm, emoji: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="📦" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">Descrizione <span className="text-gray-400">(supporta HTML)</span></label>
              <textarea rows={3} value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="Descrizione prodotto (supporta HTML: <b>, <br>, <ul>, ecc.)" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prezzo Pubblico *</label>
                <input type="number" step="0.01" value={productForm.price_public} onChange={(e) => setProductForm({ ...productForm, price_public: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prezzo Partner *</label>
                <input type="number" step="0.01" value={productForm.price_partner} onChange={(e) => setProductForm({ ...productForm, price_partner: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Range Prezzo</label>
                <input type="text" value={productForm.price_range || ''} onChange={(e) => setProductForm({ ...productForm, price_range: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="49-57" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Ordine</label>
                <input type="number" value={productForm.sort_order} onChange={(e) => setProductForm({ ...productForm, sort_order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Badge</label>
                <input type="text" value={productForm.badge || ''} onChange={(e) => setProductForm({ ...productForm, badge: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="BEST" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Link</label>
                <input type="url" value={productForm.link || ''} onChange={(e) => setProductForm({ ...productForm, link: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Colore card</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={productForm.color || '#6366f1'} onChange={(e) => setProductForm({ ...productForm, color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                  <input type="text" value={productForm.color || ''} onChange={(e) => setProductForm({ ...productForm, color: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono" placeholder="#6366f1 (vuoto = default)" />
                  {productForm.color && <button type="button" onClick={() => setProductForm({ ...productForm, color: '' })} className="text-xs text-red-500 hover:text-red-700">Reset</button>}
                </div>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={productForm.has_variants} onChange={(e) => setProductForm({ ...productForm, has_variants: e.target.checked })} className="w-4 h-4 rounded" />
                  <span className="text-sm">Ha varianti</span>
                </label>
              </div>
            </div>

            {/* Servizi associati */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-2">Servizi associati</label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_SERVICES.map((s) => {
                  const selected = productForm.services?.includes(s.code);
                  return (
                    <button
                      type="button"
                      key={s.code}
                      onClick={() => toggleService(s.code)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition ${
                        selected
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400'
                      }`}
                    >
                      <span>{s.emoji}</span>
                      <span>{s.name}</span>
                      {selected && <Check className="w-3.5 h-3.5" />}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">Seleziona i servizi della suite a cui questo prodotto è collegato.</p>
            </div>

            {/* Foto prodotto */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-2">Foto prodotto</label>
              <div className="flex items-start gap-4">
                <div className="w-28 h-28 rounded-lg border-2 border-dashed border-gray-300 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  {productForm.image ? (
                    <img src={productForm.image} alt="Foto prodotto" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="nfc-product-image-upload"
                  />
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="nfc-product-image-upload"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                    >
                      {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {productForm.image ? 'Cambia immagine' : 'Carica immagine'}
                    </label>
                    {productForm.image && (
                      <button
                        type="button"
                        onClick={() => setProductForm({ ...productForm, image: '' })}
                        className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Rimuovi
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">JPG, PNG, WEBP. Max 5MB.</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowProductForm(false); setEditingProduct(null); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annulla</button>
              <button onClick={saveProduct} disabled={saving || !productForm.code || !productForm.name} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                <Save className="w-4 h-4" />
                {editingProduct ? 'Aggiorna' : 'Crea'}
              </button>
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Prodotto</th>
                <th className="text-right p-3">Pubblico</th>
                <th className="text-right p-3">Partner</th>
                <th className="text-center p-3">Badge</th>
                <th className="text-center p-3">Stato</th>
                <th className="text-right p-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {products.map((p) => (
                <tr key={p.id} className={!p.is_active ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                  <td className="p-3 text-gray-400">{p.sort_order}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.image ? (
                        <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover border border-gray-200" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-lg">{p.emoji}</div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 flex items-center gap-1.5">
                          {p.image && p.emoji && <span>{p.emoji}</span>}
                          <span>{p.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 font-mono flex items-center gap-1.5">
                          {p.code}
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            p.item_type === 'service'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {p.item_type === 'service' ? '⚙️ Servizio' : '📦 Prodotto'}
                          </span>
                        </div>
                        {Array.isArray(p.services) && p.services.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {p.services.map((sc) => {
                              const svc = AVAILABLE_SERVICES.find((s) => s.code === sc);
                              return (
                                <span key={sc} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-medium">
                                  {svc ? `${svc.emoji} ${svc.name}` : sc}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right font-medium">{Number(p.price_public).toFixed(2)}</td>
                  <td className="p-3 text-right font-medium text-purple-600">{Number(p.price_partner).toFixed(2)}</td>
                  <td className="p-3 text-center">
                    {p.badge && <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">{p.badge}</span>}
                  </td>
                  <td className="p-3 text-center">
                    <button onClick={() => toggleProduct(p)} className="p-1 rounded hover:bg-gray-100" title={p.is_active ? 'Disattiva' : 'Attiva'}>
                      {p.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => {
                          setEditingProduct(p.id);
                          setProductForm({
                            code: p.code, name: p.name, description: p.description || '',
                            emoji: p.emoji || '', image: p.image || '',
                            price_public: Number(p.price_public),
                            price_partner: Number(p.price_partner), price_range: p.price_range || '',
                            has_variants: p.has_variants, badge: p.badge || '',
                            link: p.link || '', sort_order: p.sort_order || 0,
                            item_type: p.item_type || 'product',
                            color: p.color || '',
                            services: Array.isArray(p.services) ? p.services : []
                          });
                          setShowProductForm(true);
                        }}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {deleteConfirm === p.id ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => deleteProduct(p.id)} className="px-2 py-1 text-xs bg-red-600 text-white rounded">Conferma</button>
                          <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs bg-gray-200 rounded">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(p.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
